import { useEffect, useRef, useState } from 'react';

import { copy } from '@shared/copy';
import { useSessionStore } from '@shared/auth/session';
import { SignOutIcon } from '@shared/ui/icons/nav';

import { chromeCopy } from './copy';

/**
 * The account menu carries the session and nothing else — the operator's name,
 * the host the session was issued on, and sign-out.
 *
 * `/settings` is deliberately not here: runtime configuration is a work surface
 * each service publishes, not an account preference, so it stays in the
 * navigation column. `/inbox` is not here either, for the opposite reason — its
 * indicator announces state continuously, and an indicator inside a closed
 * disclosure is an indicator nobody sees.
 *
 * A native `<details>`/`<summary>` carries the open state and announces it for
 * free. Two behaviours the element does not carry are added: `Esc` closes and
 * returns focus to the summary, and an outside click closes. It is **not** a
 * focus trap — the menu does not suspend the page, and trapping `Tab` inside it
 * would fight the reader instead of serving them.
 */
export function AccountMenu({ signOut }: { signOut: () => void }) {
  const profile = useSessionStore((state) => state.profile);
  const role = useSessionStore((state) => state.role);
  const rootRef = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function close() {
      const root = rootRef.current;
      if (root) root.open = false;
      setOpen(false);
    }

    function onDocumentClick(event: MouseEvent) {
      const root = rootRef.current;
      if (!root?.open) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      close();
    }

    // `Esc` is listened for on the document rather than on the disclosure: the
    // element is not interactive, and a keyboard listener on it would only fire
    // while focus sits inside — which is not where a reader pressing `Esc`
    // always is.
    function onDocumentKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      const root = rootRef.current;
      if (!root?.open) return;
      event.preventDefault();
      close();
      root.querySelector('summary')?.focus();
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, []);

  const displayName = profile?.displayName ?? '';
  const roleWord = role === 'admin' ? chromeCopy.account.admin : chromeCopy.account.operator;

  return (
    <details
      ref={rootRef}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      /*
       * The extra inline-start step exists at the base tier only, where the bar
       * runs its narrowest gap: the account face and the create action are the
       * two adjacent targets a thumb reaches for, and the create action is the
       * one press on this bar that leaves the page. From `--bp-sm` the bar's own
       * gap is already wider than this and the margin goes.
       */
      className="relative ms-1 shrink-0 sm:ms-0"
    >
      <summary
        aria-label={chromeCopy.account.label(displayName)}
        className="hit-target flex cursor-pointer list-none items-center justify-center rounded-sm open:bg-active-bg hover:bg-hover-bg [&::-webkit-details-marker]:hidden"
      >
        {/*
          A neutral gradient rather than an initial: an Arabic letter's shape
          changes with joining, so an initial is read as a different letter.

          **`size-6`, and the digit is a design-system step rather than
          Tailwind's.** This repo REPLACES the spacing scale, so `6` is
          `--space-6` — 2 rem — and the `size-8` this carried resolved to
          `--space-8`, 3 rem: a face drawn *larger than the 2.75 rem target
          under it*, with a ring of pixels that looked pressable and was not,
          crowding the create action next to it on a 375 px bar. The target
          itself is untouched; only the ink inside it is.
        */}
        <span
          aria-hidden="true"
          className="block size-6 rounded-full border border-border [background:linear-gradient(135deg,color-mix(in_srgb,var(--brand-noir)_78%,var(--brand-bone)_22%),color-mix(in_srgb,var(--brand-noir)_92%,var(--brand-bone)_8%))]"
        />
      </summary>

      <nav
        aria-label={chromeCopy.account.panel}
        /*
         * The width is an arbitrary value rather than `w-60`: the replaced
         * spacing scale has no `60`, so that class emitted no declaration and
         * the panel shrink-wrapped to whichever of the operator's name and the
         * host string happened to be longer.
         */
        className="absolute z-50 rounded-md border border-border bg-surface-overlay p-2 shadow-3 [inline-size:15rem] [inset-block-start:calc(100%+var(--space-2))] [inset-inline-end:0]"
      >
        <div className="mb-2 border-b border-border px-3 pb-3 pt-2">
          <p className="m-0">{displayName}</p>
          <p className="m-0 text-xs text-ink-muted">
            {roleWord} · <bdi className="mono">{window.location.host}</bdi>
          </p>
        </div>

        <button
          type="button"
          onClick={signOut}
          className="hit-target flex w-full items-center gap-3 rounded-sm px-3 py-2 text-start text-sm text-ink-muted transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text"
        >
          <SignOutIcon />
          <span>{copy.action.signOut}</span>
        </button>
      </nav>
    </details>
  );
}
